import { GRANULARITY } from '@adtrackify/at-tracking-event-types';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { getAccountTimezone } from '@/store/account/timezone-cache';
import { normalizeAttributionDimension } from '@/features/attribution/helpers/attribution-helpers';
import * as Logger from '@/lib/monitoring/logger';
import {
  createAccountScopedStorage,
  getCurrentAccountId,
  setCurrentAccountId,
} from '@/store/account/zustand-account-storage';
import {
  getCompareDateRange,
  getDateRangeFromPreset,
} from '@/lib/helpers/dates/date-ranges';
import type { DateRangeState } from '@/lib/types/date-range-types';
import { safeLocalStorageRemove } from '@/lib/helpers/browser/safe-storage';

import { FILTER_DEBOUNCE_MS } from '../constants/dashboard-filter-constants';
import { DashboardFilterState, ViewState } from '../types/dashboard-filter-types';
import { getDefaultViewState } from '../helpers/get-default-view-state';
import {
  rememberRehydratedViewIds,
  rememberUnifiedViewId,
  wasUnifiedViewRehydrated as readUnifiedViewSession,
} from '../helpers/unified-view-session-tracker';
import {
  createAttributionActions,
} from './filter-actions/create-attribution-actions';
import {
  createCompareActions,
} from './filter-actions/create-compare-actions';
import {
  createDateRangeActions,
} from './filter-actions/date-range-actions';
import { validateGranularity } from '../helpers/filters/granularity-helpers';
import {
  createSelectionActions,
} from './filter-actions/create-selection-actions';
import {
  createViewFilterActions,
} from './filter-actions/create-view-filter-actions';
import {
  createViewDimensionActions,
} from './filter-actions/create-view-dimension-actions';
import {
  createViewHydrationActions,
} from './filter-actions/create-view-hydration-actions';
import { debounce } from '@/features/dashboard/helpers/debounce';

export function wasUnifiedViewRehydrated(viewId: string): boolean {
  return readUnifiedViewSession(viewId);
}

// ---------------------------------------------------------------------------
// Account-Specific Storage
// ---------------------------------------------------------------------------

const STORE_BASE_NAME = '@upstack/unified-dashboard';

const STORE_SCHEMA_VERSION = 6;

const dashboardStorage = createAccountScopedStorage({
  baseName: STORE_BASE_NAME,
});

type PersistedViewStateProbe = Pick<
  ViewState,
  'granularity' | 'dateRange' | 'enableCompareDate' | 'preset' | 'comparePreset' | 'attributionModel' | 'attributionWindow'
>;

function isValidViewState(state: unknown): boolean {
  if (!state || typeof state !== 'object') {
    return false;
  }

  const requiredFields = [
    'granularity',
    'dateRange',
    'enableCompareDate',
    'preset',
    'comparePreset',
    'attributionModel',
    'attributionWindow',
  ] as const;
  const obj = state as Partial<PersistedViewStateProbe>;

  for (const field of requiredFields) {
    if (!(field in obj)) {
      return false;
    }
  }

  if (!obj.dateRange || typeof obj.dateRange !== 'object') {
    return false;
  }

  const dateRange = obj.dateRange;
  if (typeof dateRange.start !== 'string' || typeof dateRange.end !== 'string') {
    return false;
  }

  return true;
}

function isValidDateRange(value: unknown): value is DateRangeState {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const obj = value as { start?: unknown; end?: unknown };
  return typeof obj.start === 'string' && typeof obj.end === 'string';
}

function migrateViewState(
  oldState: Partial<ViewState>,
  viewId?: string
): ViewState {
  const defaults = getDefaultViewState(viewId);

  const validDateRange = isValidDateRange(oldState.dateRange)
    ? oldState.dateRange
    : defaults.dateRange;

  const validCompareDateRange =
    oldState.compareDateRange === null ||
    isValidDateRange(oldState.compareDateRange)
      ? oldState.compareDateRange ?? null
      : null;

  return {
    ...defaults,
    ...oldState,
    granularity: oldState.granularity ?? defaults.granularity,
    dateRange: validDateRange,
    compareDateRange: validCompareDateRange,
    enableCompareDate: oldState.enableCompareDate ?? false,
    preset: oldState.preset ?? defaults.preset,
    comparePreset: oldState.comparePreset ?? defaults.comparePreset,
    attributionModel: oldState.attributionModel ?? defaults.attributionModel,
    attributionWindow: oldState.attributionWindow ?? defaults.attributionWindow,
    refundDateAttribution: oldState.refundDateAttribution,
    includeToday: oldState.includeToday ?? true,
    pacing: oldState.pacing ?? false,
    // Canonical `filters` is the only filter shape carried forward — legacy
    // persisted state from older schemas is dropped during migration.
    filters: oldState.filters,
    // Legacy ADSET/AD → TERM/CONTENT on rehydrate (H3).
    ...(oldState.dimension
      ? { dimension: normalizeAttributionDimension(oldState.dimension) }
      : {}),
  };
}

function migratePersistedState(
  persistedState: unknown,
  version: number
): { activeViewId: string; views: Record<string, ViewState> } {
  const defaultState = {
    activeViewId: 'default',
    views: { default: getDefaultViewState() },
  };

  if (!persistedState || typeof persistedState !== 'object') {
    return defaultState;
  }

  const state = persistedState as {
    activeViewId?: string;
    views?: Record<string, Partial<ViewState>>;
  };

  const needsMigration = version < STORE_SCHEMA_VERSION;
  const loadedViews = state.views ?? {};
  const migratedViews: Record<string, ViewState> = {};

  for (const [viewId, viewState] of Object.entries(loadedViews)) {
    const isValid = isValidViewState(viewState);
    if (needsMigration || !isValid) {
      migratedViews[viewId] = migrateViewState(viewState, viewId);
    } else {
      const view = viewState as ViewState;
      // Always normalize dimension on load even when schema version is current
      // (persisted ADSET/AD from before C1 must not re-emit to API/URL).
      migratedViews[viewId] = view.dimension
        ? {
            ...view,
            dimension: normalizeAttributionDimension(view.dimension),
          }
        : view;
    }
  }

  if (!migratedViews.default) {
    migratedViews.default = getDefaultViewState();
  }

  if (needsMigration) {
    Logger.info('Migrated dashboard store schema', {
      fromVersion: version,
      toVersion: STORE_SCHEMA_VERSION,
    });
  }

  return {
    activeViewId: state.activeViewId ?? 'default',
    views: migratedViews,
  };
}

function isSameDateRange(a: DateRangeState | null, b: DateRangeState | null): boolean {
  if (!a || !b) return false;
  return a.start === b.start && a.end === b.end;
}

function hasPresetDateRangeDrifted(view: ViewState, timezone: string): boolean {
  if (!view.preset || view.preset === 'custom') return false;
  const includeToday = view.includeToday ?? true;
  const freshRange = getDateRangeFromPreset(view.preset, timezone, includeToday);
  return !isSameDateRange(freshRange, view.dateRange ?? null);
}

function revalidatePresetDateRanges(views: Record<string, ViewState>): void {
  const timezone = getAccountTimezone();
  const recalculateDateRanges = useUnifiedDashboardStore.getState().recalculateDateRanges;

  Object.keys(views).forEach(viewId => {
    if (hasPresetDateRangeDrifted(views[viewId], timezone)) {
      Logger.info('Rehydrated view preset range is stale, recalculating', {
        viewId,
        preset: views[viewId].preset,
        persistedRange: views[viewId].dateRange,
      });
      recalculateDateRanges(viewId);
    }
  });
}

let lastAppliedAccountId: string | null = null;
let hasAppliedAccountId = false;

export function setDashboardStoreAccountId(accountId: string | null): void {
  if (hasAppliedAccountId && lastAppliedAccountId === accountId) {
    Logger.debug('Same account, skipping', { accountId });
    return;
  }

  Logger.debug('setDashboardStoreAccountId called', {
    newAccountId: accountId,
    previousAccountId: lastAppliedAccountId,
  });

  const previousAccountId = lastAppliedAccountId;
  hasAppliedAccountId = true;
  lastAppliedAccountId = accountId;

  setCurrentAccountId(accountId);

  if (accountId) {
    // Rehydrating with nothing stored keeps the state in memory, which here is the
    // previous account's. Start the new account from defaults instead.
    if (previousAccountId && dashboardStorage.getItem(STORE_BASE_NAME) === null) {
      useUnifiedDashboardStore.setState({
        activeViewId: 'default',
        views: { default: getDefaultViewState() },
      });
    }
    void useUnifiedDashboardStore.persist.rehydrate();
  } else {
    const currentViews = useUnifiedDashboardStore.getState().views;
    const fallbackViews = {
      ...currentViews,
      default: currentViews.default ?? getDefaultViewState(),
    };
    useUnifiedDashboardStore.setState({
      activeViewId: 'default',
      views: fallbackViews,
    });
  }

  Logger.debug('Dashboard store account updated', {
    accountId,
    activeViewId: useUnifiedDashboardStore.getState().activeViewId,
  });
}

// ---------------------------------------------------------------------------
// Store Interface
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useUnifiedDashboardStore = create<DashboardFilterState>()(
  devtools(
    persist(
      immer((set, get) => {
      // Compose slices
      const dateRangeActions = createDateRangeActions(set);
      const compareActions = createCompareActions(set);
      const hydrationActions = createViewHydrationActions(set, get);
      const attributionActions = createAttributionActions(set);
      const selectionActions = createSelectionActions(set, get);
      const viewFilterActions = createViewFilterActions(set);
      const viewDimensionActions = createViewDimensionActions(set);

      const applyGranularity = debounce((viewId: string, newGranularity: GRANULARITY) => {
        set(state => {
          const view = state.views[viewId];
          if (!view) return;

          view.granularity = validateGranularity(
            newGranularity,
            view.dateRange,
            'requested for > 14 days'
          );
        });
        rememberUnifiedViewId(viewId);
      }, FILTER_DEBOUNCE_MS);

      return {
        activeViewId: 'default',
        views: {
          default: getDefaultViewState(),
        },

        // ---------------------------------------------------------------
        // Sliced actions
        // ---------------------------------------------------------------

        ...dateRangeActions,
        ...compareActions,
        ...hydrationActions,
        ...attributionActions,
        ...selectionActions,
        ...viewFilterActions,
        ...viewDimensionActions,

        // ---------------------------------------------------------------
        // View management
        // ---------------------------------------------------------------

        setActiveView: viewId => {
          const viewKey = String(viewId);
          set(state => {
            state.activeViewId = viewId;
            if (!state.views[viewKey]) {
              state.views[viewKey] = getDefaultViewState(viewKey);
            }
          });
        },

        releaseView: viewId => {
          set(state => {
            if (viewId === state.activeViewId || viewId === 'default') return;
            delete state.views[viewId];
          });
        },

          getStateForView: viewId => {
            const state = get();
            return state.views[viewId] || getDefaultViewState(viewId);
          },

          // ---------------------------------------------------------------
          // Granularity
          // ---------------------------------------------------------------

        setGranularity: (viewId, newGranularity) => {
          applyGranularity(viewId, newGranularity);
        },

          // ---------------------------------------------------------------
          // Generic view-state update
          // ---------------------------------------------------------------

          updateViewState: (viewId, updates: Partial<ViewState>) => {
            set(state => {
              if (!state.views[viewId]) {
                state.views[viewId] = getDefaultViewState(viewId);
              }
              const currentView = state.views[viewId];
              const newUpdates = { ...updates };

              // Separate granularity update to apply validation
              if ('granularity' in newUpdates) {
                const requestedGranularity = newUpdates.granularity;
                delete newUpdates.granularity;

                const futureDateRange = newUpdates.dateRange || currentView.dateRange;
                currentView.granularity = validateGranularity(
                  requestedGranularity as GRANULARITY,
                  futureDateRange,
                  'requested via updateViewState for > 14 days'
                );
              }

              const timezone = getAccountTimezone();

              if (updates.preset && updates.preset !== 'custom') {
                const includeToday = updates.includeToday ?? currentView.includeToday ?? true;
                newUpdates.dateRange = getDateRangeFromPreset(updates.preset, timezone, includeToday);
                if (
                  currentView.enableCompareDate &&
                  currentView.comparePreset &&
                  currentView.comparePreset !== 'custom'
                ) {
                  newUpdates.compareDateRange = getCompareDateRange(
                    newUpdates.dateRange,
                    currentView.comparePreset,
                    timezone
                  );
                } else if (currentView.enableCompareDate && !currentView.comparePreset) {
                  newUpdates.compareDateRange = getCompareDateRange(
                    newUpdates.dateRange,
                    'samePeriodLastYear',
                    timezone
                  );
                }
              } else if (
                updates.dateRange &&
                currentView.enableCompareDate &&
                currentView.comparePreset &&
                currentView.comparePreset !== 'custom'
              ) {
                newUpdates.compareDateRange = getCompareDateRange(
                  updates.dateRange,
                  currentView.comparePreset,
                  timezone
                );
              }

              if (
                updates.comparePreset &&
                updates.comparePreset !== 'custom' &&
                currentView.enableCompareDate
              ) {
                const basePrimaryRange = updates.dateRange || currentView.dateRange;
                newUpdates.compareDateRange = getCompareDateRange(
                  basePrimaryRange,
                  updates.comparePreset,
                  timezone
                );
              }

              // Handle toggling compare on/off
              if ('enableCompareDate' in updates) {
                if (updates.enableCompareDate === true) {
                  const basePrimaryRange = updates.dateRange || currentView.dateRange;
                  const comparePresetToUse =
                    updates.comparePreset || currentView.comparePreset || 'samePeriodLastYear';
                  if (comparePresetToUse !== 'custom') {
                    newUpdates.compareDateRange = getCompareDateRange(
                      basePrimaryRange,
                      comparePresetToUse,
                      timezone
                    );
                  } else {
                    newUpdates.compareDateRange = currentView.compareDateRange || null;
                  }
                  newUpdates.comparePreset = comparePresetToUse;
                } else if (updates.enableCompareDate === false) {
                  newUpdates.compareDateRange = null;
                }
              }

              Object.assign(state.views[viewId], newUpdates);
            });
        },

        updateCurrentViewState: (updates: Partial<ViewState>) => {
            const viewId = String(get().activeViewId);

            // Handle granularity separately using the dedicated action
            if ('granularity' in updates) {
              get().setGranularity(viewId, updates.granularity as GRANULARITY);
              const { granularity: _granularity, ...restUpdates } = updates;
              updates = restUpdates;
            }

            if ('preset' in updates && updates.preset !== 'custom') {
              get().setDatePreset(viewId, updates.preset ?? '');
            } else if ('dateRange' in updates && updates.dateRange) {
              get().setCustomDateRange(viewId, updates.dateRange);
            }

            if ('comparePreset' in updates && updates.comparePreset !== 'custom') {
              get().setComparePreset(viewId, updates.comparePreset ?? '');
            } else if ('compareDateRange' in updates && updates.compareDateRange !== undefined) {
              if (updates.compareDateRange !== null) {
                get().setCustomCompareDateRange(viewId, updates.compareDateRange);
              } else {
                get().updateViewState(viewId, { compareDateRange: null } as Partial<ViewState>);
              }
            }

            if ('enableCompareDate' in updates && typeof updates.enableCompareDate === 'boolean') {
              get().toggleCompare(viewId, updates.enableCompareDate);
            }

            const {
              preset: _preset,
              dateRange: _dateRange,
              comparePreset: _comparePreset,
              compareDateRange: _compareDateRange,
              enableCompareDate: _enableCompareDate,
              ...otherUpdates
            } = updates;

            if (Object.keys(otherUpdates).length > 0) {
            get().updateViewState(
              viewId,
              otherUpdates as Partial<
                Omit<
                  ViewState,
                  | 'dateRange'
                  | 'compareDateRange'
                  | 'preset'
                  | 'comparePreset'
                  | 'enableCompareDate'
                >
              >
            );
          }
        },

        // ---------------------------------------------------------------
        // Lifecycle
        // ---------------------------------------------------------------

        destroy: () => {
          rememberRehydratedViewIds(undefined);
          set(
            () => ({
              activeViewId: 'default',
              views: {
                default: getDefaultViewState(),
              },
            }),
            false,
            'DESTROY_UNIFIED_DASHBOARD_STORE'
          );

          const accountId = getCurrentAccountId();
          if (accountId) {
            const storageKey = `${STORE_BASE_NAME}-${accountId}`;
            const removed = safeLocalStorageRemove(storageKey);
            if (!removed) {
              Logger.warn('Failed to remove dashboard store from localStorage', {});
            }
          }
        },
      };
    }),
    {
      name: STORE_BASE_NAME,
      version: STORE_SCHEMA_VERSION,
      storage: createJSONStorage(() => dashboardStorage),
      migrate: migratePersistedState,
      partialize: (state) => ({
        activeViewId: state.activeViewId,
        views: state.views,
      }),
      onRehydrateStorage: () => (state, error) => {
        if (error || !state) {
          rememberRehydratedViewIds(undefined);
          return;
        }
        rememberRehydratedViewIds(state.views);
        revalidatePresetDateRanges(state.views);
      },
    }
    ),
    { enabled: process.env.NODE_ENV === 'development' }
  )
);
